import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  BenefitProductItem,
  BenefitProductPlanItem,
  PayrollBenefitsAssignmentService,
} from '../../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import {
  BenefitCategoryTab,
  buildPayrollBenefitSectionDefinitions,
  PayrollBenefitSectionDefinition,
  PayrollBenefitSubcategoryDefinitionsByCategory,
} from '../../../services/payroll-benefits-assignment/payroll-benefit-categories';
import {
  PayrollDeductionProductMapping,
  PayrollDeductionImportRecord,
  PayrollDeductionImportRefreshResponse,
  PayrollDeductionRule,
  PayrollDeductionSetup,
  PayrollDeductionSetupCode,
  PayrollProviderConnectionResponse,
  PayrollService,
} from '../../../services/payroll/payroll.service';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../../../services/session/session.service';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { UsersService } from '../../../services/users/users.service';
import { PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG } from '../payroll-admin-feature-flags';

interface BenefitSection {
  id: string;
  title: string;
  products: BenefitProductItem[];
}

type ProductVisibilityFilter = 'selectedByTeam' | 'all';
type PayrollDeductionsTopTab = 'setup' | 'history';

const REPLAYABLE_IMPORT_STATUSES = new Set([
  'failed',
  'needs_review',
  'status_error',
  'submission_failed',
]);

@Component({
  selector: 'app-payroll-deductions',
  templateUrl: './payroll-deductions.component.html',
  styleUrls: ['./payroll-deductions.component.scss'],
})
export class PayrollDeductionsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly alphabeticalCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  @Input() companyId = '';

  protected isInitialCompanyCheckComplete = false;
  protected isLoading = false;
  protected isDeductionSetupRefreshing = false;
  protected hasLoadedLiveDeductionSetup = false;
  protected isSaving = false;
  protected hasCompany = false;
  protected isPayrollDeductionFeatureEnabled = false;

  protected company: any;
  protected benefitProducts: BenefitProductItem[] = [];
  protected subcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {};
  protected availableDeductionCodes: PayrollDeductionSetupCode[] = [];
  protected selectedAllowedCodes = new Set<string>();
  protected productPayrollDeductionMap: Record<string, PayrollDeductionProductMapping> = {};
  protected enabledProductMappingIds = new Set<string>();
  protected selectedByTeamProductIds = new Set<string>();
  protected productVisibilityFilter: ProductVisibilityFilter = 'selectedByTeam';
  protected providerConfigured = false;
  protected providerSyncError = '';
  protected ukgConfigured = false;
  protected ukgSyncError = '';
  protected payrollProviderConnection: PayrollProviderConnectionResponse | null = null;
  protected payrollDeductionSetupUnsupportedReason = '';
  protected deductionSyncHistoryUnsupportedReason = '';
  protected deductionSyncReplayUnsupportedReason = '';
  protected deductionSyncStatusRefreshUnsupportedReason = '';
  protected isCodesPanelExpanded = false;
  protected isMappingsPanelExpanded = true;
  protected expandedMappingCategories = new Set<BenefitCategoryTab>();
  protected expandedMappingSections = new Set<string>();
  protected expandedAdvancedProductIds = new Set<string>();
  protected matrixBulkCodeByKey: Record<string, string> = {};
  protected activeTopTab: PayrollDeductionsTopTab = 'setup';
  protected importHistoryItems: PayrollDeductionImportRecord[] = [];
  protected importHistoryLoading = false;
  protected importHistoryError = '';
  protected importHistoryPage = 0;
  protected importHistoryLimit = 25;
  protected importHistoryTotal = 0;
  protected selectedImport: PayrollDeductionImportRecord | null = null;
  protected replayingImportId = '';
  protected refreshingImportStatusId = '';

  protected readonly categories: Array<{ id: BenefitCategoryTab; title: string }> = [
    { id: 'health', title: 'Health' },
    { id: 'supplemental', title: 'Supplemental' },
    { id: 'retirement', title: 'Retirement' },
    { id: 'pnc', title: 'P&C' },
    { id: 'other', title: 'Other' },
  ];

  private currentUserReady = false;
  private meSubscription?: Subscription;
  private initialAllowedCodesSnapshot = '';
  private initialProductPayrollDeductionMapSnapshot = '';
  private loadDataRequestId = 0;

  constructor(
    private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
    private payrollService: PayrollService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private sessionService: SessionService,
    private teamCompaniesService: TeamCompaniesService,
    private teamsService: TeamsService,
    private usersService: UsersService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me) {
        return;
      }

      this.currentUserReady = true;
      const cachedCompanies = this.usersService.getCachedTeamCompanies();
      const providedCompanyId = `${this.companyId || ''}`.trim();
      if (providedCompanyId) {
        this.hasCompany = true;
        this.isInitialCompanyCheckComplete = true;
      } else if (cachedCompanies) {
        this.hasCompany = cachedCompanies.length > 0;
        this.company = cachedCompanies[0];
        this.companyId = this.company?.id || '';
        this.isInitialCompanyCheckComplete = true;
      }

      try {
        await this.loadData();
      } finally {
        this.isInitialCompanyCheckComplete = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.currentUserReady) {
      return;
    }
    if (changes.companyId) {
      await this.loadData();
    }
  }

  protected get selectedAllowedCodesCount(): number {
    return this.selectedAllowedCodes.size;
  }

  protected get mappedProductsCount(): number {
    const visibleProductIds = new Set(this.getVisibleBenefitProducts().map((product) => product.id));
    return Object.entries(this.productPayrollDeductionMap || {})
      .filter(([productId, config]) => visibleProductIds.has(productId) && this.mappingHasAnyDeductionCode(config))
      .length;
  }

  protected get selectedByTeamProductsCount(): number {
    return this.getPayrollDeductionAllowedBenefitProducts()
      .filter((product) => this.isProductSelectedByTeam(product)).length;
  }

  protected get visibleBenefitProductsCount(): number {
    return this.getVisibleBenefitProducts().length;
  }

  protected get payrollDeductionAllowedProductsCount(): number {
    return this.getPayrollDeductionAllowedBenefitProducts().length;
  }

  protected get hasUnsavedChanges(): boolean {
    return this.serializeAllowedCodes() !== this.initialAllowedCodesSnapshot
      || this.serializeProductPayrollDeductionMap() !== this.initialProductPayrollDeductionMapSnapshot;
  }

  protected get isDeductionSetupEditDisabled(): boolean {
    return this.isSaving || this.isDeductionSetupRefreshing || (
      this.shouldLoadPayrollDeductionSetup() && !this.hasLoadedLiveDeductionSetup
    );
  }

  protected get approvedDeductionCodes(): PayrollDeductionSetupCode[] {
    return this.availableDeductionCodes.filter((code) => this.selectedAllowedCodes.has(code.code));
  }

  protected get activePayrollProviderName(): string {
    return this.payrollProviderConnection?.provider?.name
      || this.payrollProviderConnection?.connection?.displayName
      || 'Payroll provider';
  }

  protected get connectionRequiredTitle(): string {
    return this.getPayrollProviderSlug() === 'ukg_ready'
      ? 'Connect UKG Ready first'
      : `Connect ${this.activePayrollProviderName} first`;
  }

  protected get connectionRequiredCopy(): string {
    return this.getPayrollProviderSlug() === 'ukg_ready'
      ? 'Payroll deduction setup depends on the employer\'s UKG Ready connection so we can review available deduction codes.'
      : `Payroll deduction setup depends on the employer's ${this.activePayrollProviderName} connection so we can review available deduction codes.`;
  }

  protected get providerSyncIssueTitle(): string {
    return this.getPayrollProviderSlug() === 'ukg_ready'
      ? 'UKG sync issue.'
      : `${this.activePayrollProviderName} sync issue.`;
  }

  protected get providerSavedCodesWarning(): string {
    return this.getPayrollProviderSlug() === 'ukg_ready'
      ? 'Existing saved codes are still shown, but new approvals may be limited until UKG is reachable again.'
      : `Existing saved codes are still shown, but new approvals may be limited until ${this.activePayrollProviderName} is reachable again.`;
  }

  protected get isUkgReadySetup(): boolean {
    return this.getPayrollProviderSlug() === 'ukg_ready';
  }

  protected get noDeductionDescriptionText(): string {
    return this.getPayrollProviderSlug() === 'ukg_ready'
      ? 'No description from UKG'
      : `No description from ${this.activePayrollProviderName}`;
  }

  protected get importHistoryPageStart(): number {
    if (this.importHistoryTotal === 0) {
      return 0;
    }
    return (this.importHistoryPage * this.importHistoryLimit) + 1;
  }

  protected get importHistoryPageEnd(): number {
    return Math.min(
      this.importHistoryTotal,
      (this.importHistoryPage + 1) * this.importHistoryLimit,
    );
  }

  protected get hasPreviousImportHistoryPage(): boolean {
    return this.importHistoryPage > 0;
  }

  protected get hasNextImportHistoryPage(): boolean {
    return this.importHistoryPageEnd < this.importHistoryTotal;
  }

  protected async setActiveTopTab(tab: PayrollDeductionsTopTab): Promise<void> {
    this.activeTopTab = tab;
    if (tab === 'history') {
      await this.loadImportHistory();
    }
  }

  protected onGoToCompanyTab(): void {
    void this.router.navigate(['payrolladmin', 'company', 'company']);
  }

  protected onGoToConnectionsTab(): void {
    void this.router.navigate(['payrolladmin', 'company', 'connections']);
  }

  protected onGoToBenefitsPage(): void {
    void this.router.navigate(['payrolladmin', 'benefits']);
  }

  protected toggleCodesPanel(): void {
    this.isCodesPanelExpanded = !this.isCodesPanelExpanded;
  }

  protected toggleMappingsPanel(): void {
    this.isMappingsPanelExpanded = !this.isMappingsPanelExpanded;
  }

  protected isMappingCategoryExpanded(categoryId: BenefitCategoryTab): boolean {
    return this.expandedMappingCategories.has(categoryId);
  }

  protected toggleMappingCategory(categoryId: BenefitCategoryTab): void {
    const next = new Set(this.expandedMappingCategories);
    if (next.has(categoryId)) {
      next.delete(categoryId);
    } else {
      next.add(categoryId);
    }
    this.expandedMappingCategories = next;
  }

  protected isMappingSectionExpanded(categoryId: BenefitCategoryTab, sectionId: string): boolean {
    return this.expandedMappingSections.has(this.buildMappingSectionKey(categoryId, sectionId));
  }

  protected toggleMappingSection(categoryId: BenefitCategoryTab, sectionId: string): void {
    const key = this.buildMappingSectionKey(categoryId, sectionId);
    const next = new Set(this.expandedMappingSections);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedMappingSections = next;
  }

  protected isDeductionCodeApproved(code: PayrollDeductionSetupCode): boolean {
    return this.selectedAllowedCodes.has(code.code);
  }

  protected getDeductionCodeStatusLabel(code: PayrollDeductionSetupCode): string {
    if (this.isDeductionCodeSavedOnly(code)) {
      return 'Saved only';
    }
    if (code.preTax) {
      return 'Pre-tax';
    }
    return 'Post-tax';
  }

  protected isDeductionCodeSavedOnly(code: PayrollDeductionSetupCode): boolean {
    const isCurrentProviderCode = code.isCurrentProviderCode ?? code.isCurrentUkgCode;
    return isCurrentProviderCode === false;
  }

  protected getMappedProductCountForCode(code: PayrollDeductionSetupCode): number {
    return Object.values(this.productPayrollDeductionMap)
      .filter((config) => this.getDeductionCodesUsedByMapping(config).includes(code.code))
      .length;
  }

  protected isAllowedCodeToggleDisabled(code: PayrollDeductionSetupCode): boolean {
    return this.isDeductionSetupEditDisabled || (
      this.isDeductionCodeApproved(code) && this.getMappedProductCountForCode(code) > 0
    );
  }

  protected onAllowedCodeToggle(code: PayrollDeductionSetupCode, change: MatCheckboxChange): void {
    if (!change.checked && this.getMappedProductCountForCode(code) > 0) {
      return;
    }

    const next = new Set(this.selectedAllowedCodes);
    if (change.checked) {
      next.add(code.code);
    } else {
      next.delete(code.code);
    }
    this.selectedAllowedCodes = next;
  }

  protected onProductVisibilityFilterChanged(filter: ProductVisibilityFilter): void {
    this.productVisibilityFilter = filter;
    this.syncExpandedMappingsState();
  }

  protected isProductSelectedByTeam(product: BenefitProductItem): boolean {
    return this.selectedByTeamProductIds.has(product.id);
  }

  protected getBenefitSections(category: BenefitCategoryTab): BenefitSection[] {
    const products = this.getVisibleBenefitProducts().filter((product) => product.mapping.category === category);
    const sectionDefinitions: PayrollBenefitSectionDefinition[] = buildPayrollBenefitSectionDefinitions(
      category,
      this.subcategoryDefinitionsByCategory,
      products.map((product) => product.mapping),
    );

    const sections: BenefitSection[] = sectionDefinitions.map((section) => ({
      id: section.id,
      title: section.title,
      products: section.subcategory
        ? this.sortBenefitProducts(products.filter((product) => product.mapping.subcategory === section.subcategory))
        : this.sortBenefitProducts([...products]),
    }));

    const assignedProductIds = new Set(
      sections.reduce((all: string[], section) => all.concat(section.products.map((product) => product.id)), []),
    );
    const uncategorizedProducts = products.filter((product) => !assignedProductIds.has(product.id));
    if (uncategorizedProducts.length > 0 || sections.length === 0) {
      sections.push({
        id: 'general',
        title: 'General',
        products: this.sortBenefitProducts(uncategorizedProducts),
      });
    }

    return sections.filter((section) => section.products.length > 0);
  }

  protected trackCategory(index: number, category: { id: BenefitCategoryTab }): string {
    return category.id;
  }

  protected trackSection(index: number, section: BenefitSection): string {
    return section.id;
  }

  protected trackProduct(index: number, product: BenefitProductItem): string {
    return product.id;
  }

  protected trackDeductionCode(index: number, code: PayrollDeductionSetupCode): string {
    return code.code;
  }

  protected getProductDeductionCode(productId: string): string {
    return `${this.productPayrollDeductionMap[productId]?.deductionCode || ''}`.trim();
  }

  protected isProductPayrollDeductionMappingEnabled(product: BenefitProductItem): boolean {
    return this.enabledProductMappingIds.has(product.id);
  }

  protected isProductMappingControlDisabled(product: BenefitProductItem): boolean {
    return this.isDeductionSetupEditDisabled || !this.isProductPayrollDeductionMappingEnabled(product);
  }

  protected onProductMappingEnabledToggle(product: BenefitProductItem, change: MatCheckboxChange): void {
    if (this.isDeductionSetupEditDisabled) {
      return;
    }

    const nextEnabledProductMappingIds = new Set(this.enabledProductMappingIds);
    if (change.checked) {
      nextEnabledProductMappingIds.add(product.id);
      this.enabledProductMappingIds = nextEnabledProductMappingIds;
      return;
    }

    nextEnabledProductMappingIds.delete(product.id);
    this.enabledProductMappingIds = nextEnabledProductMappingIds;

    const nextMap = {
      ...(this.productPayrollDeductionMap || {}),
    };
    delete nextMap[product.id];
    this.productPayrollDeductionMap = nextMap;
    this.expandedAdvancedProductIds.delete(product.id);
    this.clearProductBulkCodes(product.id);
  }

  protected hasProductPlanOptions(product: BenefitProductItem): boolean {
    return Array.isArray(product?.plans) && product.plans.length > 0;
  }

  protected isAdvancedEditorOpen(product: BenefitProductItem): boolean {
    return this.expandedAdvancedProductIds.has(product.id);
  }

  protected toggleAdvancedEditor(product: BenefitProductItem): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const next = new Set(this.expandedAdvancedProductIds);
    if (next.has(product.id)) {
      next.delete(product.id);
    } else {
      next.add(product.id);
    }
    this.expandedAdvancedProductIds = next;
  }

  protected getProductMappingStateLabel(product: BenefitProductItem): string {
    if (!this.isProductPayrollDeductionMappingEnabled(product)) {
      return 'Not enabled';
    }

    const mapping = this.productPayrollDeductionMap[product.id];
    const hasDefault = !!`${mapping?.deductionCode || ''}`.trim();
    const rules = this.getProductDeductionRules(product.id);
    if (!hasDefault && rules.length === 0) {
      return 'Ready to map';
    }
    if (rules.some((rule) => !!`${rule?.tier || ''}`.trim())) {
      return 'Plan/tier-specific';
    }
    if (rules.length > 0) {
      return 'Plan-specific';
    }
    return 'Default code';
  }

  protected getProductPlanRows(product: BenefitProductItem): BenefitProductPlanItem[] {
    return [...(product?.plans || [])];
  }

  protected getProductTierColumns(product: BenefitProductItem): string[] {
    const tiers = this.normalizeStringArray(
      (product?.plans || []).reduce((values: string[], plan) => {
        const coverageTiers = Array.isArray(plan.coverageTiers) ? plan.coverageTiers : [];
        return values.concat(coverageTiers.length > 0 ? coverageTiers : [plan.tier]);
      }, []),
    );
    return tiers.length > 0 ? tiers : [''];
  }

  protected getTierDisplayLabel(tier: string): string {
    switch (`${tier || ''}`.trim().toUpperCase()) {
      case 'EMPL':
        return 'Member Only';
      case 'SPSE':
        return 'Member plus Spouse';
      case 'SPF':
        return 'Member plus Children';
      case 'DPDT':
        return 'Member plus Family';
      case 'PLUS1':
        return 'Member plus One';
      default:
        return `${tier || ''}`.trim() || 'Code';
    }
  }

  protected getPlanDisplayLabel(plan: BenefitProductPlanItem): string {
    return `${plan?.name || plan?.planLabels?.[0] || plan?.planIds?.[0] || plan?.id || ''}`.trim();
  }

  protected getMatrixDeductionCode(
    product: BenefitProductItem,
    plan: BenefitProductPlanItem,
    tier: string,
  ): string {
    return `${this.findDeductionRule(product.id, plan, tier)?.deductionCode || ''}`.trim();
  }

  protected onMatrixDeductionCodeChanged(
    product: BenefitProductItem,
    plan: BenefitProductPlanItem,
    tier: string,
    deductionCode: string,
  ): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const normalizedCode = `${deductionCode || ''}`.trim();
    const nextRules = this.getProductDeductionRules(product.id)
      .filter((rule) => !this.isRuleForPlanTier(rule, plan, tier));
    if (normalizedCode) {
      nextRules.push(this.buildDeductionRule(plan, tier, normalizedCode));
    }
    this.setProductMapping(product.id, {
      ...(this.productPayrollDeductionMap[product.id] || {}),
      deductionRules: nextRules,
    });
  }

  protected getAdvancedMissingCombinationCount(product: BenefitProductItem): number {
    const mapping = this.productPayrollDeductionMap[product.id];
    if (!!`${mapping?.deductionCode || ''}`.trim() || this.getProductDeductionRules(product.id).length === 0) {
      return 0;
    }
    return this.getProductPlanTierCombinations(product)
      .filter((combination) => !this.getMatrixDeductionCode(product, combination.plan, combination.tier))
      .length;
  }

  protected getBulkCode(productId: string, scope: string): string {
    return `${this.matrixBulkCodeByKey[this.buildBulkKey(productId, scope)] || ''}`.trim();
  }

  protected onBulkCodeChanged(productId: string, scope: string, deductionCode: string): void {
    if (!this.enabledProductMappingIds.has(productId) || this.isDeductionSetupEditDisabled) {
      return;
    }

    this.matrixBulkCodeByKey = {
      ...this.matrixBulkCodeByKey,
      [this.buildBulkKey(productId, scope)]: `${deductionCode || ''}`.trim(),
    };
  }

  protected getPlanBulkScope(plan: BenefitProductPlanItem): string {
    return `plan:${this.getPlanKey(plan)}`;
  }

  protected applyBulkCodeToAll(product: BenefitProductItem): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const deductionCode = this.getBulkCode(product.id, 'all');
    if (!deductionCode) {
      return;
    }
    this.getProductPlanTierCombinations(product)
      .forEach((combination) => this.onMatrixDeductionCodeChanged(product, combination.plan, combination.tier, deductionCode));
  }

  protected applyBulkCodeToPlan(product: BenefitProductItem, plan: BenefitProductPlanItem): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const deductionCode = this.getBulkCode(product.id, this.getPlanBulkScope(plan));
    if (!deductionCode) {
      return;
    }
    this.getProductTierColumns(product)
      .forEach((tier) => this.onMatrixDeductionCodeChanged(product, plan, tier, deductionCode));
  }

  protected hasProductPlanTierCodes(product: BenefitProductItem): boolean {
    return this.getProductDeductionRules(product.id).length > 0;
  }

  protected getPlanTierCodeSummary(product: BenefitProductItem): string {
    const rules = this.getProductDeductionRules(product.id);
    if (rules.length === 0) {
      return '';
    }

    const missingCount = this.getAdvancedMissingCombinationCount(product);
    const ruleLabel = rules.length === 1 ? 'plan/tier code' : 'plan/tier codes';
    if (missingCount > 0) {
      return `${rules.length} ${ruleLabel} set, ${missingCount} missing`;
    }
    if (this.getProductDeductionCode(product.id)) {
      const overrideLabel = rules.length === 1 ? 'plan/tier override' : 'plan/tier overrides';
      return `${rules.length} ${overrideLabel} set`;
    }
    return `${rules.length} ${ruleLabel} set`;
  }

  protected clearProductPlanTierCodes(product: BenefitProductItem): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const nextMapping = {
      ...(this.productPayrollDeductionMap[product.id] || {}),
    };
    delete nextMapping.deductionRules;
    this.setProductMapping(product.id, nextMapping);
  }

  protected getMappedProductCountForSection(section: BenefitSection): number {
    return section.products.filter((product) =>
      this.mappingHasAnyDeductionCode(this.productPayrollDeductionMap[product.id])).length;
  }

  protected onProductDeductionCodeChanged(product: BenefitProductItem, deductionCode: string): void {
    if (this.isProductMappingControlDisabled(product)) {
      return;
    }

    const normalizedCode = `${deductionCode || ''}`.trim();
    const existingMapping = this.productPayrollDeductionMap[product.id] || {};
    if (!normalizedCode) {
      const nextMapping = { ...existingMapping };
      delete nextMapping.deductionCode;
      delete nextMapping.deductionCodeDescription;
      delete nextMapping.deductionCodeId;
      this.setProductMapping(product.id, nextMapping);
      return;
    }

    const matchedCode = this.availableDeductionCodes.find((code) => code.code === normalizedCode);
    this.setProductMapping(product.id, {
      ...existingMapping,
      deductionCode: normalizedCode,
      deductionCodeDescription: matchedCode?.description || '',
      deductionCodeId: matchedCode?.deductionCodeId ?? null,
    });
  }

  protected resetChanges(): void {
    this.selectedAllowedCodes = new Set(this.parseAllowedCodesSnapshot(this.initialAllowedCodesSnapshot));
    this.productPayrollDeductionMap = JSON.parse(this.initialProductPayrollDeductionMapSnapshot || '{}');
    this.enabledProductMappingIds = this.buildEnabledProductMappingIds(this.productPayrollDeductionMap);
    this.expandedAdvancedProductIds = new Set(
      [...this.expandedAdvancedProductIds].filter((productId) => this.enabledProductMappingIds.has(productId)),
    );
    this.matrixBulkCodeByKey = {};
  }

  protected async save(): Promise<void> {
    if (this.isDeductionSetupEditDisabled || !this.companyId || !this.isPayrollDeductionFeatureEnabled) {
      return;
    }

    const advancedValidationError = this.getAdvancedMappingValidationError();
    if (advancedValidationError) {
      this.snackBar.open(advancedValidationError, 'Close', {
        duration: 5000,
      });
      return;
    }

    this.isSaving = true;
    try {
      const result = await this.payrollService.updateCompanyPayrollDeductionSetup(
        this.companyId,
        {
          allowedDeductionCodes: this.approvedDeductionCodes.map((code) => ({
            code: code.code,
            description: code.description,
            deductionCodeId: code.deductionCodeId,
            preTax: code.preTax,
          })),
          productPayrollDeductionMap: JSON.parse(this.serializeProductPayrollDeductionMap() || '{}'),
        },
        this.getPayrollProviderSlug(),
      );
      this.applySetup(result);
      this.snackBar.open('Saved payroll deduction setup.', 'Close', {
        duration: 3000,
      });
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.error || error?.error?.message || error?.message || 'Unable to save payroll deduction setup.',
        'Close',
        { duration: 4000 },
      );
    } finally {
      this.isSaving = false;
    }
  }

  protected async refreshImportHistory(): Promise<void> {
    await this.loadImportHistory(this.importHistoryPage, true, true);
  }

  protected async goToPreviousImportHistoryPage(): Promise<void> {
    if (!this.hasPreviousImportHistoryPage) {
      return;
    }
    await this.loadImportHistory(this.importHistoryPage - 1, true);
  }

  protected async goToNextImportHistoryPage(): Promise<void> {
    if (!this.hasNextImportHistoryPage) {
      return;
    }
    await this.loadImportHistory(this.importHistoryPage + 1, true);
  }

  protected async openImportDetails(item: PayrollDeductionImportRecord): Promise<void> {
    if (!this.companyId || !item?.id) {
      return;
    }
    this.selectedImport = item;
    try {
      this.selectedImport = await this.payrollService.getCompanyPayrollDeductionImport(this.companyId, item.id);
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.error || error?.error?.message || error?.message || 'Unable to load import details.',
        'Close',
        { duration: 4000 },
      );
    }
  }

  protected closeImportDetails(): void {
    this.selectedImport = null;
  }

  protected canReplayImport(item: PayrollDeductionImportRecord | null | undefined): boolean {
    return !this.deductionSyncReplayUnsupportedReason
      && REPLAYABLE_IMPORT_STATUSES.has(`${item?.status || ''}`.trim().toLowerCase());
  }

  protected canRefreshImportStatus(item: PayrollDeductionImportRecord | null | undefined): boolean {
    return !this.deductionSyncStatusRefreshUnsupportedReason
      && Boolean(`${item?.ukgImportJobId || ''}`.trim());
  }

  protected async refreshImportStatus(item: PayrollDeductionImportRecord | null | undefined, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.companyId || !item?.id || !this.canRefreshImportStatus(item) || this.refreshingImportStatusId) {
      return;
    }

    this.refreshingImportStatusId = item.id;
    try {
      const refreshedImport = await this.payrollService.refreshCompanyPayrollDeductionImportStatus(
        this.companyId,
        item.id,
      );
      this.importHistoryItems = this.importHistoryItems.map((historyItem) => (
        historyItem.id === refreshedImport.id
          ? { ...historyItem, ...refreshedImport }
          : historyItem
      ));
      if (this.selectedImport?.id === refreshedImport.id) {
        this.selectedImport = {
          ...this.selectedImport,
          ...refreshedImport,
        };
      }
      const status = `${refreshedImport.status || ''}`.trim().toLowerCase();
      this.snackBar.open(
        status === 'status_error'
          ? 'Unable to refresh sync status. Details were updated with the error.'
          : 'Refreshed sync status.',
        'Close',
        { duration: status === 'status_error' ? 5000 : 3000 },
      );
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.error || error?.error?.message || error?.message || 'Unable to refresh sync status.',
        'Close',
        { duration: 5000 },
      );
    } finally {
      this.refreshingImportStatusId = '';
    }
  }

  protected async replayImport(item: PayrollDeductionImportRecord | null | undefined, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.companyId || !item?.id || !this.canReplayImport(item) || this.replayingImportId) {
      return;
    }

    const reason = window.prompt('Reason for replay');
    if (reason === null) {
      return;
    }

    this.replayingImportId = item.id;
    try {
      const replayedImport = await this.payrollService.replayCompanyPayrollDeductionImport(
        this.companyId,
        item.id,
        reason,
      );
      this.selectedImport = replayedImport;
      await this.loadImportHistory(this.importHistoryPage, true);
      this.snackBar.open('Replayed payroll deduction sync.', 'Close', {
        duration: 3000,
      });
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.error || error?.error?.message || error?.message || 'Unable to replay sync.',
        'Close',
        { duration: 5000 },
      );
    } finally {
      this.replayingImportId = '';
    }
  }

  protected trackImportRecord(index: number, item: PayrollDeductionImportRecord): string {
    return item.id || `${index}`;
  }

  protected formatImportAction(action: string | undefined): string {
    const normalizedAction = `${action || ''}`.trim().toLowerCase();
    if (normalizedAction === 'cancel') {
      return 'Cancel';
    }
    if (normalizedAction === 'upsert') {
      return 'Upsert';
    }
    return `${action || ''}`.trim() || 'Import';
  }

  protected formatImportStatus(status: string | undefined): string {
    return `${status || ''}`
      .trim()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase()) || 'Unknown';
  }

  protected getImportStatusClass(status: string | undefined): string {
    const normalizedStatus = `${status || ''}`.trim().toLowerCase();
    if (['completed', 'accepted', 'processing', 'unpollable_submitted'].includes(normalizedStatus)) {
      return 'status-chip--ok';
    }
    if (REPLAYABLE_IMPORT_STATUSES.has(normalizedStatus)) {
      return 'status-chip--warning';
    }
    return '';
  }

  protected formatImportPerson(item: PayrollDeductionImportRecord): string {
    const name = `${item.firstName || ''} ${item.lastName || ''}`.trim();
    return name || item.email || item.userId || 'Unknown user';
  }

  protected formatImportProduct(item: PayrollDeductionImportRecord): string {
    const productLabel = [item.vendorName, item.productName].filter(Boolean).join(' - ');
    return productLabel || item.vendorProductId || 'Unknown product';
  }

  protected formatMoneyCents(value: number | null | undefined): string {
    const amount = Number(value || 0) / 100;
    return amount.toLocaleString(undefined, {
      currency: 'USD',
      style: 'currency',
    });
  }

  protected formatDateTime(value: string | null | undefined): string {
    const normalizedValue = `${value || ''}`.trim();
    if (!normalizedValue) {
      return 'Not set';
    }
    const date = new Date(normalizedValue);
    if (Number.isNaN(date.getTime())) {
      return normalizedValue;
    }
    return date.toLocaleString();
  }

  protected formatEffectiveRange(item: PayrollDeductionImportRecord): string {
    const from = `${item.effectiveFrom || ''}`.trim();
    const to = `${item.effectiveTo || ''}`.trim();
    return [from, to].filter(Boolean).join(' to ') || 'Not set';
  }

  protected formatJson(value: any): string {
    if (value === undefined || value === null || value === '') {
      return 'None';
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (_) {
      return `${value}`;
    }
  }

  private getProductDeductionRules(productId: string): PayrollDeductionRule[] {
    return this.normalizeDeductionRules(this.productPayrollDeductionMap[productId]?.deductionRules);
  }

  private normalizeDeductionRules(rules: any): PayrollDeductionRule[] {
    return (Array.isArray(rules) ? rules : [])
      .map((rule): PayrollDeductionRule | null => {
        const deductionCode = `${rule?.deductionCode || ''}`.trim();
        const planId = `${rule?.planId || ''}`.trim();
        const planLabel = `${rule?.planLabel || ''}`.trim();
        const tier = `${rule?.tier || ''}`.trim();
        if (!deductionCode || (!planId && !planLabel && !tier)) {
          return null;
        }
        return {
          ...(planId ? { planId } : {}),
          ...(planLabel ? { planLabel } : {}),
          ...(tier ? { tier } : {}),
          deductionCode,
          ...(rule?.deductionCodeDescription ? { deductionCodeDescription: `${rule.deductionCodeDescription}`.trim() } : {}),
          ...(rule?.deductionCodeId !== undefined && rule?.deductionCodeId !== null
            ? { deductionCodeId: rule.deductionCodeId }
            : {}),
        };
      })
      .filter((rule): rule is PayrollDeductionRule => !!rule);
  }

  private mappingHasAnyDeductionCode(mapping: PayrollDeductionProductMapping | undefined): boolean {
    return this.getDeductionCodesUsedByMapping(mapping).length > 0;
  }

  private getDeductionCodesUsedByMapping(mapping: PayrollDeductionProductMapping | undefined): string[] {
    const codes = [
      `${mapping?.deductionCode || ''}`.trim(),
      ...this.normalizeDeductionRules(mapping?.deductionRules).map((rule) => rule.deductionCode),
    ].filter((code) => !!code);
    return [...new Set(codes)];
  }

  private findDeductionRule(
    productId: string,
    plan: BenefitProductPlanItem,
    tier: string,
  ): PayrollDeductionRule | undefined {
    return this.getProductDeductionRules(productId)
      .find((rule) => this.isRuleForPlanTier(rule, plan, tier));
  }

  private isRuleForPlanTier(rule: PayrollDeductionRule, plan: BenefitProductPlanItem, tier: string): boolean {
    return this.isRuleForPlan(rule, plan)
      && this.normalizeMatchKey(rule.tier) === this.normalizeMatchKey(tier);
  }

  private isRuleForPlan(rule: PayrollDeductionRule, plan: BenefitProductPlanItem): boolean {
    const rulePlanId = this.normalizeMatchKey(rule.planId);
    const rulePlanLabel = this.normalizeMatchKey(rule.planLabel);
    const planIds = this.normalizeStringArray(plan?.planIds || [plan?.id]);
    const planLabels = this.normalizeStringArray([
      this.getPlanDisplayLabel(plan),
      ...(plan?.planLabels || []),
    ]);
    return (
      !!rulePlanId && planIds.some((planId) => this.normalizeMatchKey(planId) === rulePlanId)
    ) || (
      !!rulePlanLabel && planLabels.some((planLabel) => this.normalizeMatchKey(planLabel) === rulePlanLabel)
    );
  }

  private buildDeductionRule(
    plan: BenefitProductPlanItem,
    tier: string,
    deductionCode: string,
  ): PayrollDeductionRule {
    const matchedCode = this.availableDeductionCodes.find((code) => code.code === deductionCode);
    const planId = plan.selectionMode === 'plan-id'
      ? this.normalizeStringArray(plan.planIds || [plan.id])[0]
      : '';
    const planLabel = this.getPlanDisplayLabel(plan);
    return {
      ...(planId ? { planId } : {}),
      ...(planLabel ? { planLabel } : {}),
      ...(`${tier || ''}`.trim() ? { tier: `${tier || ''}`.trim() } : {}),
      deductionCode,
      deductionCodeDescription: matchedCode?.description || '',
      deductionCodeId: matchedCode?.deductionCodeId ?? null,
    };
  }

  private setProductMapping(productId: string, mapping: PayrollDeductionProductMapping): void {
    const normalizedMapping = this.normalizeProductMapping(mapping);
    const nextMap = {
      ...(this.productPayrollDeductionMap || {}),
    };
    if (normalizedMapping) {
      nextMap[productId] = normalizedMapping;
      this.enabledProductMappingIds = new Set([...this.enabledProductMappingIds, productId]);
    } else {
      delete nextMap[productId];
    }
    this.productPayrollDeductionMap = nextMap;
  }

  private normalizeProductMapping(
    mapping: PayrollDeductionProductMapping | undefined,
  ): PayrollDeductionProductMapping | null {
    const deductionCode = `${mapping?.deductionCode || ''}`.trim();
    const deductionRules = this.normalizeDeductionRules(mapping?.deductionRules);
    if (!deductionCode && deductionRules.length === 0) {
      return null;
    }
    return {
      ...(deductionCode
        ? {
            deductionCode,
            ...(mapping?.deductionCodeDescription
              ? { deductionCodeDescription: `${mapping.deductionCodeDescription}`.trim() }
              : {}),
            ...(mapping?.deductionCodeId !== undefined && mapping?.deductionCodeId !== null
              ? { deductionCodeId: mapping.deductionCodeId }
              : {}),
          }
        : {}),
      ...(deductionRules.length > 0 ? { deductionRules } : {}),
    };
  }

  private getProductPlanTierCombinations(product: BenefitProductItem): Array<{
    plan: BenefitProductPlanItem;
    tier: string;
  }> {
    const tiers = this.getProductTierColumns(product);
    return this.getProductPlanRows(product)
      .reduce((combinations: Array<{ plan: BenefitProductPlanItem; tier: string }>, plan) => {
        return combinations.concat(tiers.map((tier) => ({ plan, tier })));
      }, []);
  }

  private buildBulkKey(productId: string, scope: string): string {
    return `${productId}:${scope}`;
  }

  private clearProductBulkCodes(productId: string): void {
    const productPrefix = `${productId}:`;
    this.matrixBulkCodeByKey = Object.keys(this.matrixBulkCodeByKey)
      .filter((key) => !key.startsWith(productPrefix))
      .reduce((acc: Record<string, string>, key) => {
        acc[key] = this.matrixBulkCodeByKey[key];
        return acc;
      }, {});
  }

  private getPlanKey(plan: BenefitProductPlanItem): string {
    return [
      plan?.selectionMode || '',
      this.normalizeStringArray(plan?.planIds || [plan?.id])[0] || '',
      this.getPlanDisplayLabel(plan),
    ].map((value) => this.normalizeMatchKey(value)).join('|');
  }

  private normalizeMatchKey(value: any): string {
    return `${value || ''}`.trim().toLowerCase();
  }

  private normalizeStringArray(values: any[]): string[] {
    return [...new Set((values || [])
      .map((value) => `${value || ''}`.trim())
      .filter((value) => !!value))];
  }

  private getAdvancedMappingValidationError(): string {
    const productWithMissingMappings = this.getPayrollDeductionAllowedBenefitProducts().find((product) =>
      this.getAdvancedMissingCombinationCount(product) > 0);
    if (!productWithMissingMappings) {
      return '';
    }
    const productLabel = productWithMissingMappings.vendorName || productWithMissingMappings.productName || 'this product';
    const visibilityHint = this.productVisibilityFilter !== 'all' && !this.isProductVisible(productWithMissingMappings)
      ? ' Switch to All products to update it.'
      : '';
    return `Complete every plan/tier deduction code for ${productLabel}, or choose a default product-level code.${visibilityHint}`;
  }

  private async loadData(): Promise<void> {
    const requestId = ++this.loadDataRequestId;
    this.isLoading = true;
    this.isDeductionSetupRefreshing = false;
    this.hasLoadedLiveDeductionSetup = false;
    try {
      const providedCompanyId = `${this.companyId || ''}`.trim();
      if (providedCompanyId) {
        this.companyId = providedCompanyId;
        this.hasCompany = true;
      } else {
        const companies = await this.usersService.getTeamCompanies();
        this.company = companies?.[0];
        this.companyId = this.company?.id || '';
        this.hasCompany = !!this.companyId;
      }

      if (!this.companyId) {
        this.hasCompany = false;
        this.payrollProviderConnection = null;
        this.resetProviderCapabilityReasons();
        this.benefitProducts = [];
        this.subcategoryDefinitionsByCategory = {};
        this.availableDeductionCodes = [];
        this.selectedAllowedCodes = new Set<string>();
        this.productPayrollDeductionMap = {};
        this.enabledProductMappingIds = new Set<string>();
        this.selectedByTeamProductIds = new Set<string>();
        this.providerConfigured = false;
        this.providerSyncError = '';
        this.ukgConfigured = false;
        this.ukgSyncError = '';
        this.hasLoadedLiveDeductionSetup = true;
        this.expandedMappingCategories = new Set<BenefitCategoryTab>();
        this.expandedMappingSections = new Set<string>();
        this.expandedAdvancedProductIds = new Set<string>();
        this.matrixBulkCodeByKey = {};
        this.resetImportHistoryState();
        return;
      }

      this.resetImportHistoryState();

      const isFeatureEnabledPromise = this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG,
        'team-admin',
        {
          userId: this.sessionService.getUserId() || undefined,
        },
      ).catch(() => false);
      const benefitsLoadPromise = this.payrollBenefitsAssignmentService.loadBenefitProductsForCompany(this.companyId, undefined, {
        includeStripeProductEnrichment: false,
      });
      const teamsPromise = this.teamsService.getTeamsForCompany(this.companyId).catch(() => []);
      const payrollProviderConnection = await this.payrollService.getCompanyPayrollProviderConnection(this.companyId).catch(() => null);
      if (requestId !== this.loadDataRequestId) {
        return;
      }

      this.payrollProviderConnection = payrollProviderConnection;
      this.syncProviderCapabilityReasons();
      const shouldLoadSetup = this.shouldLoadPayrollDeductionSetup();
      this.isDeductionSetupRefreshing = shouldLoadSetup;
      this.hasLoadedLiveDeductionSetup = !shouldLoadSetup;
      const deductionSetupPromise = shouldLoadSetup
        ? this.payrollService.getCompanyPayrollDeductionSetup(
            this.companyId,
            this.getPayrollProviderSlug(),
            { includeLiveDeductionCodes: false },
          )
        : Promise.resolve(this.getEmptyPayrollDeductionSetup());

      const [isFeatureEnabled, benefitsLoad, teams, deductionSetup] = await Promise.all([
        isFeatureEnabledPromise,
        benefitsLoadPromise,
        teamsPromise,
        deductionSetupPromise,
      ]);
      if (requestId !== this.loadDataRequestId) {
        return;
      }
      this.isPayrollDeductionFeatureEnabled = isFeatureEnabled;
      this.company = benefitsLoad.company;
      this.benefitProducts = benefitsLoad.benefitProducts;
      this.selectedByTeamProductIds = this.buildSelectedByTeamProductIds(
        this.company,
        this.filterTeamsForCompany(teams, this.companyId),
      );
      this.subcategoryDefinitionsByCategory =
        this.teamCompaniesService.getResolvedPayrollConfig(this.company)?.payrollBenefitSubcategoryDefinitionsByCategory || {};
      this.syncExpandedMappingsState();
      this.applySetup(deductionSetup);
      if (shouldLoadSetup) {
        void this.refreshLiveDeductionSetup(requestId);
      }
      if (this.activeTopTab === 'history') {
        await this.loadImportHistory(0, true);
      }
    } catch (error) {
      this.availableDeductionCodes = [];
      this.selectedAllowedCodes = new Set<string>();
      this.productPayrollDeductionMap = {};
      this.enabledProductMappingIds = new Set<string>();
      this.selectedByTeamProductIds = new Set<string>();
      this.payrollProviderConnection = null;
      this.resetProviderCapabilityReasons();
      this.providerConfigured = false;
      this.providerSyncError = '';
      this.ukgConfigured = false;
      this.ukgSyncError = '';
      this.isDeductionSetupRefreshing = false;
      this.hasLoadedLiveDeductionSetup = true;
      this.expandedAdvancedProductIds = new Set<string>();
      this.matrixBulkCodeByKey = {};
      this.resetImportHistoryState();
      this.snackBar.open('Unable to load payroll deduction setup.', 'Close', {
        duration: 4000,
      });
    } finally {
      if (requestId === this.loadDataRequestId) {
        this.isLoading = false;
      }
    }
  }

  private async refreshLiveDeductionSetup(requestId: number): Promise<void> {
    try {
      const liveSetup = await this.payrollService.getCompanyPayrollDeductionSetup(
        this.companyId,
        this.getPayrollProviderSlug(),
        { includeLiveDeductionCodes: true },
      );
      if (requestId !== this.loadDataRequestId) {
        return;
      }
      this.applySetup(liveSetup);
      this.hasLoadedLiveDeductionSetup = true;
    } catch (_) {
      if (requestId !== this.loadDataRequestId) {
        return;
      }
      this.snackBar.open('Unable to refresh live payroll deduction codes.', 'Close', {
        duration: 4000,
      });
    } finally {
      if (requestId === this.loadDataRequestId) {
        this.isDeductionSetupRefreshing = false;
      }
    }
  }

  private applySetup(setup: PayrollDeductionSetup): void {
    this.availableDeductionCodes = [...(setup?.availableDeductionCodes || [])].sort((left, right) => {
      const leftLabel = `${left.code} ${left.description || ''}`.trim();
      const rightLabel = `${right.code} ${right.description || ''}`.trim();
      return this.alphabeticalCollator.compare(leftLabel, rightLabel);
    });
    this.selectedAllowedCodes = new Set((setup?.allowedDeductionCodes || []).map((code) => code.code));
    this.productPayrollDeductionMap = JSON.parse(JSON.stringify(setup?.productPayrollDeductionMap || {}));
    this.enabledProductMappingIds = this.buildEnabledProductMappingIds(this.productPayrollDeductionMap);
    this.providerConfigured = setup?.providerConfigured === true || setup?.ukgConfigured === true;
    this.providerSyncError = `${setup?.providerSyncError || setup?.ukgSyncError || ''}`.trim();
    this.ukgConfigured = setup?.ukgConfigured === true;
    this.ukgSyncError = `${setup?.ukgSyncError || ''}`.trim();
    this.initialAllowedCodesSnapshot = this.serializeAllowedCodes();
    this.initialProductPayrollDeductionMapSnapshot = this.serializeProductPayrollDeductionMap();
  }

  private resetImportHistoryState(): void {
    this.importHistoryItems = [];
    this.importHistoryError = '';
    this.importHistoryPage = 0;
    this.importHistoryTotal = 0;
    this.selectedImport = null;
  }

  private async loadImportHistory(
    page = this.importHistoryPage,
    forceReload = false,
    refreshStatuses = false,
  ): Promise<void> {
    if (!this.companyId || this.importHistoryLoading) {
      return;
    }
    if (!this.payrollProviderConnection) {
      this.importHistoryItems = [];
      this.importHistoryError = '';
      this.importHistoryPage = page;
      this.importHistoryTotal = 0;
      return;
    }
    if (this.deductionSyncHistoryUnsupportedReason) {
      this.importHistoryItems = [];
      this.importHistoryError = this.deductionSyncHistoryUnsupportedReason;
      this.importHistoryPage = page;
      this.importHistoryTotal = 0;
      return;
    }
    if (!forceReload && this.importHistoryItems.length > 0 && page === this.importHistoryPage) {
      return;
    }

    this.importHistoryLoading = true;
    this.importHistoryError = '';
    try {
      let refreshResult: PayrollDeductionImportRefreshResponse | null = null;
      const query = {
        limit: this.importHistoryLimit,
        page,
      };
      if (refreshStatuses) {
        if (this.deductionSyncStatusRefreshUnsupportedReason) {
          this.snackBar.open(this.deductionSyncStatusRefreshUnsupportedReason, 'Close', {
            duration: 4000,
          });
        } else {
          refreshResult = await this.payrollService.refreshCompanyPayrollDeductionImportStatuses(this.companyId, query);
        }
      }
      const result = await this.payrollService.listCompanyPayrollDeductionImports(this.companyId, {
        ...query,
      });
      this.importHistoryItems = result?.items || [];
      this.importHistoryLimit = Number(result?.limit || this.importHistoryLimit);
      this.importHistoryPage = Number(result?.page || page);
      this.importHistoryTotal = Number(result?.total || 0);
      if (this.selectedImport?.id) {
        const updatedSelected = this.importHistoryItems.find((item) => item.id === this.selectedImport?.id);
        if (updatedSelected) {
          this.selectedImport = {
            ...this.selectedImport,
            ...updatedSelected,
          };
        }
      }
      if (refreshResult) {
        this.showImportHistoryRefreshResult(refreshResult);
      }
    } catch (error: any) {
      this.importHistoryItems = [];
      this.importHistoryError =
        error?.error?.error || error?.error?.message || error?.message || 'Unable to load sync history.';
    } finally {
      this.importHistoryLoading = false;
    }
  }

  private showImportHistoryRefreshResult(result: PayrollDeductionImportRefreshResponse): void {
    if (result.failed > 0) {
      this.snackBar.open(
        `Refreshed ${result.refreshed} sync status${result.refreshed === 1 ? '' : 'es'}; ${result.failed} failed.`,
        'Close',
        { duration: 5000 },
      );
      return;
    }

    if (result.refreshed > 0) {
      this.snackBar.open(
        `Refreshed ${result.refreshed} sync status${result.refreshed === 1 ? '' : 'es'}.`,
        'Close',
        { duration: 3000 },
      );
      return;
    }

    this.snackBar.open('No sync statuses needed refresh.', 'Close', {
      duration: 3000,
    });
  }

  private shouldLoadPayrollDeductionSetup(): boolean {
    return !!this.payrollProviderConnection && !this.payrollDeductionSetupUnsupportedReason;
  }

  private getEmptyPayrollDeductionSetup(): PayrollDeductionSetup {
    return {
      allowedDeductionCodes: [],
      availableDeductionCodes: [],
      companyId: this.companyId,
      providerConfigured: false,
      providerName: this.activePayrollProviderName,
      providerSlug: this.getPayrollProviderSlug(),
      productPayrollDeductionMap: {},
      ukgConfigured: false,
    };
  }

  private syncProviderCapabilityReasons(): void {
    this.payrollDeductionSetupUnsupportedReason = this.payrollService.providerCapabilityUnsupportedReason(
      this.payrollProviderConnection,
      'supportsPayrollDeductionSetup',
      'payroll deduction setup',
    );
    this.deductionSyncHistoryUnsupportedReason = this.payrollService.providerCapabilityUnsupportedReason(
      this.payrollProviderConnection,
      'supportsDeductionImportHistory',
      'payroll deduction sync history',
    );
    this.deductionSyncReplayUnsupportedReason = this.payrollService.providerCapabilityUnsupportedReason(
      this.payrollProviderConnection,
      'supportsDeductionImportReplay',
      'payroll deduction sync replay',
    );
    this.deductionSyncStatusRefreshUnsupportedReason = this.payrollService.providerCapabilityUnsupportedReason(
      this.payrollProviderConnection,
      'supportsDeductionImportStatusRefresh',
      'payroll deduction sync status refresh',
    );
  }

  private resetProviderCapabilityReasons(): void {
    this.payrollDeductionSetupUnsupportedReason = '';
    this.deductionSyncHistoryUnsupportedReason = '';
    this.deductionSyncReplayUnsupportedReason = '';
    this.deductionSyncStatusRefreshUnsupportedReason = '';
  }

  private getPayrollProviderSlug(): string {
    return `${this.payrollProviderConnection?.providerSlug || this.payrollProviderConnection?.connection?.providerSlug || ''}`.trim();
  }

  private syncExpandedMappingsState(): void {
    const nextCategories = new Set<BenefitCategoryTab>();

    this.categories.forEach((category) => {
      const sections = this.getBenefitSections(category.id);
      if (!sections.length) {
        return;
      }

      nextCategories.add(category.id);
    });

    this.expandedMappingCategories = nextCategories;
    this.expandedMappingSections = new Set<string>();
  }

  private buildMappingSectionKey(categoryId: BenefitCategoryTab, sectionId: string): string {
    return `${categoryId}:${sectionId}`;
  }

  private serializeAllowedCodes(): string {
    return JSON.stringify([...this.selectedAllowedCodes].sort());
  }

  private parseAllowedCodesSnapshot(snapshot: string): string[] {
    try {
      const parsed = JSON.parse(snapshot || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  private serializeProductPayrollDeductionMap(): string {
    const sortedProductIds = Object.keys(this.productPayrollDeductionMap || {}).sort();
    const normalized = sortedProductIds.reduce((acc: Record<string, PayrollDeductionProductMapping>, productId) => {
      const config = this.normalizeProductMapping(this.productPayrollDeductionMap[productId]);
      if (!config) {
        return acc;
      }

      acc[productId] = {
        ...config,
        ...(config.deductionRules
          ? {
              deductionRules: [...config.deductionRules].sort((left, right) => {
                return [
                  left.planId || '',
                  left.planLabel || '',
                  left.tier || '',
                  left.deductionCode || '',
                ].join('|').localeCompare([
                  right.planId || '',
                  right.planLabel || '',
                  right.tier || '',
                  right.deductionCode || '',
                ].join('|'));
              }),
            }
          : {}),
      };
      return acc;
    }, {});

    return JSON.stringify(normalized);
  }

  private buildEnabledProductMappingIds(
    productPayrollDeductionMap: Record<string, PayrollDeductionProductMapping>,
  ): Set<string> {
    return new Set(
      Object.keys(productPayrollDeductionMap || {})
        .filter((productId) => this.mappingHasAnyDeductionCode(productPayrollDeductionMap[productId])),
    );
  }

  private sortBenefitProducts(products: BenefitProductItem[]): BenefitProductItem[] {
    return [...products].sort((left, right) => {
      const leftVendor = `${left?.vendorName || ''}`.trim();
      const rightVendor = `${right?.vendorName || ''}`.trim();
      const vendorCompare = this.alphabeticalCollator.compare(leftVendor, rightVendor);
      if (vendorCompare !== 0) {
        return vendorCompare;
      }

      return this.alphabeticalCollator.compare(
        `${left?.productName || ''}`.trim(),
        `${right?.productName || ''}`.trim(),
      );
    });
  }

  private getVisibleBenefitProducts(): BenefitProductItem[] {
    const payrollDeductionAllowedProducts = this.getPayrollDeductionAllowedBenefitProducts();
    if (this.productVisibilityFilter === 'all') {
      return payrollDeductionAllowedProducts;
    }
    return payrollDeductionAllowedProducts.filter((product) => this.isProductSelectedByTeam(product));
  }

  private getPayrollDeductionAllowedBenefitProducts(): BenefitProductItem[] {
    return this.benefitProducts.filter((product) => product?.payrollDeductionAllowed !== false);
  }

  private isProductVisible(product: BenefitProductItem): boolean {
    return this.getVisibleBenefitProducts().some((visibleProduct) => visibleProduct.id === product.id);
  }

  private buildSelectedByTeamProductIds(company: any, teams: any[]): Set<string> {
    const availableProductIds = new Set((this.benefitProducts || []).map((product) => product.id));
    const selectedProductIds = (teams || []).reduce((ids: string[], team: any) => {
      return ids.concat(this.payrollBenefitsAssignmentService.getSelectedMappedBenefitProductIds(company, team));
    }, []);

    return new Set(
      this.normalizeStringArray(selectedProductIds)
        .filter((productId) => availableProductIds.has(productId)),
    );
  }

  private filterTeamsForCompany(teams: any[], companyId: string): any[] {
    if (!Array.isArray(teams)) {
      return [];
    }
    const withCompanyId = teams.filter((team) => !!team?.companyId);
    if (withCompanyId.length === 0) {
      return teams;
    }
    return teams.filter((team) => team?.companyId === companyId);
  }
}
